import { useState } from 'react';
import { Plus, Unlink } from 'lucide-react';
import {
  useCoursePositions,
  useLinkPositionTraining,
  useUnlinkPositionTraining,
  useUpdatePositionTraining,
  usePositions,
} from '@/api/course-builder';
import { EmptyState } from '@/components/shared/empty-state';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { companyName } from '@/lib/org-labels';
import { LinkPositionForm } from './link-position-form';
import type { PositionTraining } from '@/types';

type Props = { courseId: number };

export function PositionTrainingSection({ courseId }: Props) {
  const { data: linkedPositions = [] } = useCoursePositions(courseId);
  const { data: positionsData } = usePositions();
  const linkMut = useLinkPositionTraining();
  const unlinkMut = useUnlinkPositionTraining();
  const updateMut = useUpdatePositionTraining();

  const [showLinkForm, setShowLinkForm] = useState(false);
  const [unlinkingPosition, setUnlinkingPosition] = useState<PositionTraining | null>(null);
  const [editingPriority, setEditingPriority] = useState<number | null>(null);
  const [priorityValue, setPriorityValue] = useState(0);

  const allPositions = positionsData?.data ?? [];
  const linkedIds = new Set(linkedPositions.map((lp) => lp.position.id));
  const availablePositions = allPositions.filter((p) => !linkedIds.has(p.id));

  const handleUpdatePriority = (positionId: number) => {
    if (editingPriority !== positionId) return;
    updateMut.mutate(
      { courseId, positionId, data: { priority: priorityValue } },
      { onSuccess: () => setEditingPriority(null) },
    );
  };

  const handleUnlink = () => {
    if (!unlinkingPosition) return;
    unlinkMut.mutate(
      { courseId, positionId: unlinkingPosition.position.id },
      { onSuccess: () => setUnlinkingPosition(null) },
    );
  };

  return (
    <div className="mb-8">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Puestos vinculados</h3>
        <button
          onClick={() => setShowLinkForm(true)}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80"
        >
          <Plus className="h-3.5 w-3.5" /> Vincular puesto
        </button>
      </div>

      {showLinkForm && (
        <LinkPositionForm
          availablePositions={availablePositions}
          isPending={linkMut.isPending}
          error={linkMut.error}
          onSubmit={(data) =>
            linkMut.mutate({ courseId, data }, { onSuccess: () => setShowLinkForm(false) })
          }
          onCancel={() => setShowLinkForm(false)}
        />
      )}

      {linkedPositions.length === 0 ? (
        <EmptyState
          title="Sin puestos vinculados"
          description="Vincula puestos a esta capacitación para definir la matriz puesto-capacitación"
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Puesto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Departamento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Obligatorio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Prioridad
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {linkedPositions.map((lp) => (
                <tr key={lp.id} className="hover:bg-muted/30">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {lp.position.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {lp.position.department.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {companyName(lp.position.department.company)}
                  </td>
                  <td className="px-6 py-4">
                    {lp.is_required ? (
                      <span className="rounded bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-700 ring-1 ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-400/20">
                        Obligatorio
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Opcional</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {editingPriority === lp.position.id ? (
                      <input
                        type="number"
                        min={0}
                        value={priorityValue}
                        onChange={(e) => setPriorityValue(Number(e.target.value))}
                        onBlur={() => handleUpdatePriority(lp.position.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdatePriority(lp.position.id);
                          if (e.key === 'Escape') setEditingPriority(null);
                        }}
                        autoFocus
                        className="w-20 rounded border border-input bg-background px-2 py-1 text-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPriority(lp.position.id);
                          setPriorityValue(lp.priority);
                        }}
                        className="rounded px-1 py-0.5 text-left hover:bg-muted hover:text-primary"
                        title="Click para editar"
                      >
                        {lp.priority}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => setUnlinkingPosition(lp)}
                      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      title="Desvincular"
                      aria-label="Desvincular"
                    >
                      <Unlink className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unlinkingPosition && (
        <ConfirmDialog
          title="Desvincular puesto"
          message={`¿Desvincular el puesto "${unlinkingPosition.position.name}" de esta capacitación?`}
          confirmLabel="Desvincular"
          isPending={unlinkMut.isPending}
          onConfirm={handleUnlink}
          onCancel={() => setUnlinkingPosition(null)}
        />
      )}
    </div>
  );
}
